import React, { useState, useEffect, useRef, useCallback } from "react";
import { Form, Button, Card, Spinner, Row, Col, Modal, OverlayTrigger, Popover, Tooltip, Badge } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { AGCText } from "../../generic-components/AGCText";
import { AGCSelect } from "../../generic-components/AGCSelect";
import { AGCDynamicSelect } from "../../generic-components/AGCDynamicSelect";
import { AGCDatePicker } from "../../generic-components/AGCDatePicker";
import { AGCTextArea } from "../../generic-components/AGCTextArea";
import { apiGenericCreate, apiGenericUpdate, genericGetOne, genericGetAll, getNextTicketNumber, getCurrentTicketNumber, getImagesForEntry, saveExistingImage, getAllOrgSettings, getOrgInfo } from "../generic/crud";
import { computeRoundOffAmount } from "../../utils/CurrencyUtils";
import { useVehicleCapture } from "../../hooks/useVehicleCapture";
import { WeighbridgeCaptureModal } from "../../components/weighbridge/WeighbridgeCaptureModal";
import { API_URL } from "../../config/api.config";
import { ENTITY_ID_CUSTOMER_SALE, ENTITY_ID_SALES_CUSTOMER, ENTITY_ID_INVENTORY_ITEM, ENTITY_ID_USER, ENTITY_ID_VEHICLE, ENTITY_ID_TAX_PLAN } from "../generic/Constants";
import { EwayBillReadinessPanel, EwayUpsellCard, EwayBillOutcomeCard, buildEwayChecks } from "./EwayBillReadinessPanel";
import { EwayBillDetailDialog } from "./EwayBillDetailDialog";
import { ShipToAddressSelect } from "./ShipToAddressSelect";
import { EwayDecisionsPreview } from "./EwayDecisionsPreview";
import { defaultRawCustomerSale } from "../generic/InitialStates";
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from "../../utils/toast/Toast";
import { SaleItemsSection } from "./SaleItemsSection";
import { SalePaymentsSection } from "./SalePaymentsSection";
import { useAuth } from "../../modules/auth";
import { formatDateTime } from "../../utils/Utils";

export function SaleEntryForm({ id, readOnly = false }) {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [taxPlansLookup, setTaxPlansLookup] = useState({});
    const [showPaymentWarning, setShowPaymentWarning] = useState(false);
    const [paymentWarningMessage, setPaymentWarningMessage] = useState("");
    const [showValidationError, setShowValidationError] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    // Store original data from backend to preserve id, orgId, and other base fields during update
    const [originalData, setOriginalData] = useState(null);
    // Store users list for sales person lookup
    const [usersList, setUsersList] = useState([]);
    // Vehicle capture states
    const { capture, isCapturing, weight: weightTrack, image: imageTrack, groupSelectModal } = useVehicleCapture(true);
    const [capturedLoadedImage, setCapturedLoadedImage] = useState(null);
    // Images for view mode
    const [savedImages, setSavedImages] = useState([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    // Track whether ticket number is a preview (not yet committed)
    const [isTicketPreview, setIsTicketPreview] = useState(false);
    // Org settings for hardware control
    const [weighbridgeEnabled, setWeighbridgeEnabled] = useState(true);
    const [cameraEnabled, setCameraEnabled] = useState(true);
    // Controls visibility of the invoice creation toggle
    const [showInvoiceCreationSelection, setShowInvoiceCreationSelection] = useState(true);
    // Controls whether price can be edited for product/service items
    const [allowPriceEditProduct, setAllowPriceEditProduct] = useState(false);
    const [allowPriceEditService, setAllowPriceEditService] = useState(false);
    const [allowSalesCorrection, setAllowSalesCorrection] = useState(false);
    const [roundOffMode, setRoundOffMode] = useState('CEILING');
    // GST Services add-on: null while loading, then true/false from the synced org record
    const [gstEnabled, setGstEnabled] = useState(() => {
        const cached = localStorage.getItem("gstServicesEnabled");
        return cached === null ? null : cached === "true";
    });
    const [orgRecord, setOrgRecord] = useState(null);
    // Full customer record for the e-Way readiness checks (GSTIN, pin code, address)
    const [ewayCustomer, setEwayCustomer] = useState(null);
    // E-Way bill detail dialog on the view page (for sales that already have an outcome)
    const [ewayDetailOpen, setEwayDetailOpen] = useState(false);

    // Set default form data with current user as sales person
    const getDefaultFormData = useCallback(() => {
        return {
            ...defaultRawCustomerSale,
            salesPersonId: currentUser ? {
                id: currentUser.id || currentUser._id,
                name: currentUser.name || currentUser.fullName || currentUser.userName || currentUser.email
            } : null
        };
    }, [currentUser]);

    const [formData, setFormData] = useState(getDefaultFormData);

    // Refs for quick navigation
    const vehicleNumberRef = useRef(null);
    // Set during render (below) — read inside onSubmit so a sale with a failing
    // e-Way readiness check cannot be saved.
    const ewayBlockedRef = useRef(false);
    const customerRef = useRef(null);
    // Track the previous vehicle ID to detect changes
    const previousVehicleIdRef = useRef(null);

    // Validation schema
    const validationSchema = Yup.object({
        vehicle: Yup.object().nullable().required("Vehicle is required"),
        salesCustomer: Yup.object().nullable().required("Customer is required"),
        emptyWeight: Yup.number().nullable().positive("Must be positive"),
        loadedWeight: Yup.number().nullable().positive("Must be positive"),
        leadKm: Yup.number().nullable().min(0, "Cannot be negative"),
        roundOffAmount: Yup.number().nullable(),
    });

    // Formik setup
    const formik = useFormik({
        initialValues: formData,
        validationSchema: validationSchema,
        enableReinitialize: true,
        onSubmit: async (values) => {
            // Validate items
            if (!values.itemsList || values.itemsList.length === 0) {
                showToast("Please add at least one item", TOAST_FAILURE);
                return;
            }

            // A failing e-Way bill check means the paid generation WILL be rejected —
            // fix the data or turn the switch off before saving.
            if (ewayBlockedRef.current) {
                showToast("E-Way bill check has failing issues — fix them or turn off Generate E-Way Bill", TOAST_FAILURE);
                return;
            }

            // Validate photo is captured when camera is enabled
            if (cameraEnabled) {
                const hasNewImage = capturedLoadedImage?.base64Image;
                const hasSavedImage = savedImages.some(img => img.imageType === 'LOADED_WEIGHT');
                if (!hasNewImage && !hasSavedImage) {
                    showToast("Please capture the vehicle image before saving", TOAST_FAILURE);
                    return;
                }
            }

            setIsSaving(true);
            try {
                // For new sales or partial records, assign the real ticket number now (at save time)
                let finalTicketNumber = values.ticketSequenceNumber || "";
                if (!id || (originalData?.isPartialRecord === true && !originalData?.ticketSequenceNumber)) {
                    // Never fall back to the preview number - it may already belong to another sale.
                    try {
                        const billResponse = await getNextTicketNumber();
                        finalTicketNumber = billResponse?.data?.formattedNumber;
                    } catch (error) {
                        console.error("Error fetching final ticket number at save time:", error);
                        finalTicketNumber = "";
                    }
                    if (!finalTicketNumber) {
                        showToast("Could not get a ticket number. The sale was not saved - please try again.", TOAST_FAILURE);
                        return;
                    }
                }

                // Prepare data for submission - only include fields the backend expects
                const now = new Date().toISOString();
                const saleType = originalData?.saleType || "ONE_STEP";
                const submitData = {
                    ticketSequenceNumber: finalTicketNumber,
                    // salesSecondStepDate IS the sale date, for both sale types.
                    salesSecondStepDate: now,
                    // The weigh-in stamp only follows it on a ONE_STEP sale, where the two
                    // stamps are the same instant by definition. For a TWO_STEP sale we omit
                    // it entirely - it is a weighbridge fact, and the backend merge leaves
                    // the recorded weigh-in untouched.
                    ...(saleType === "TWO_STEP" ? {} : { salesFirstStepDate: now }),
                    // Extract vehicleNumber from vehicle object
                    vehicleNumber: values.vehicle?.name || values.vehicle?.vehicleNumber || values.vehicleNumber || "",
                    emptyWeight: values.emptyWeight,
                    loadedWeight: values.loadedWeight,
                    loadedWeightCorrection: parseFloat(values.loadedWeightCorrection) || 0,
                    destination: values.destination || "",
                    leadKm: values.leadKm,
                    driverName: values.driverName || "",
                    customerPhoneNumber: values.customerPhoneNumber || "",
                    remarks: values.remarks || "",
                    roundOffAmount: parseFloat(values.roundOffAmount) || 0,
                    battaAmount: parseFloat(values.battaAmount) || 0,
                    // Mark as complete (not partial) when saving from full form
                    isPartialRecord: false,
                    // Preserve saleType from original record, default to ONE_STEP for new sales
                    saleType: originalData?.saleType || "ONE_STEP",
                    // Whether to create an invoice (include tax in calculations)
                    createInvoice: values.createInvoice || false,
                    // An e-Way bill request only makes sense along with an invoice
                    generateEwayBill: (values.createInvoice && values.generateEwayBill) || false,
                    // Same for the e-Invoice, which additionally needs a GST-registered customer
                    generateEinvoice: (values.createInvoice && values.generateEinvoice) || false,
                    // Which of the customer's shipping addresses the load goes to (Bill To / Ship To);
                    // null means the bill ships to the customer's billing address
                    shipToAddressId: (values.createInvoice
                        && (values.generateEwayBill || values.generateEinvoice)
                        && values.shipToAddressId) || null,
                    // Handle salesCustomer - send as object with id and name
                    salesCustomer: values.salesCustomer ? {
                        id: values.salesCustomer.id || values.salesCustomer.value,
                        name: values.salesCustomer.name || values.salesCustomer.label
                    } : null,
                    // Handle salesPersonId - extract ID if it's an object
                    salesPersonId: typeof values.salesPersonId === 'object' && values.salesPersonId !== null
                        ? (values.salesPersonId?.id || values.salesPersonId?._id || values.salesPersonId?.value || "")
                        : (values.salesPersonId || ""),
                    // Items list
                    itemsList: values.itemsList || [],
                    // Payments list
                    paymentsList: values.paymentsList || [],
                };

                if (id && originalData) {
                    // For updates, include id and orgId from the original record
                    submitData.id = originalData.id || id;
                    submitData.orgId = originalData.orgId;
                    // Preserve other base document fields
                    if (originalData.createdOn) submitData.createdOn = originalData.createdOn;
                    if (originalData.createdBy) submitData.createdBy = originalData.createdBy;
                    if (originalData.synced !== undefined) submitData.synced = originalData.synced;

                    await apiGenericUpdate(ENTITY_ID_CUSTOMER_SALE, submitData, id);

                    // Save captured loaded image if there's one pending
                    const updatePrimaryId = originalData?.primaryId || id;
                    if (capturedLoadedImage && !capturedLoadedImage.saved && updatePrimaryId && capturedLoadedImage.base64Image) {
                        try {
                            await saveExistingImage(
                                'SALE',
                                updatePrimaryId,
                                'LOADED_WEIGHT',
                                capturedLoadedImage.base64Image,
                                capturedLoadedImage.contentType || 'image/jpeg'
                            );
                        } catch (imgError) {
                            console.error('Failed to save captured image:', imgError);
                        }
                    }

                    showToast("Sale updated successfully!", TOAST_SUCCESS);
                    // Navigate to print summary after update
                    navigate(`/sale-print/${id}`);
                } else {
                    const response = await apiGenericCreate(ENTITY_ID_CUSTOMER_SALE, submitData);
                    showToast("Sale created successfully!", TOAST_SUCCESS);
                    // Handle various response structures from the API
                    const responseData = response?.data;
                    const newSaleId = responseData?.id || responseData?._id || responseData?.data?.id || responseData?.data?._id;
                    const newSalePrimaryId = responseData?.primaryId || responseData?.data?.primaryId || newSaleId;

                    // Save captured image if there's one that hasn't been saved yet
                    if (capturedLoadedImage && !capturedLoadedImage.saved && newSalePrimaryId && capturedLoadedImage.base64Image) {
                        try {
                            await saveExistingImage(
                                'SALE',
                                newSalePrimaryId,
                                'LOADED_WEIGHT',
                                capturedLoadedImage.base64Image,
                                capturedLoadedImage.contentType || 'image/jpeg'
                            );
                            console.log('Image saved for new sale:', newSalePrimaryId);
                        } catch (imgError) {
                            console.error('Failed to save captured image:', imgError);
                            // Don't fail the sale creation, just log the error
                        }
                    }

                    // Navigate to print summary after creation
                    if (newSaleId) {
                        navigate(`/sale-print/${newSaleId}`);
                    } else {
                        // Fallback: navigate to sales list if ID not found
                        console.warn('Could not extract sale ID from response:', response);
                        navigate('/sales-list');
                    }
                }
            } catch (error) {
                showToast("Error saving sale: " + (error.response?.data || error.message), TOAST_FAILURE);
            } finally {
                setIsSaving(false);
            }
        },
    });

    // Load org settings for edit mode too
    useEffect(() => {
        if (id) {
            getAllOrgSettings().then((res) => {
                const settings = res?.data || [];
                const getBool = (name, def) => { const s = settings.find(s => s.typeName === name); return s ? (s.value === true || s.value === 'true') : def; };
                setWeighbridgeEnabled(getBool('ENABLE_WEIGHBRIDGE_CAPTURE', true));
                setCameraEnabled(getBool('ENABLE_CAMERA_CAPTURE', true));
                setShowInvoiceCreationSelection(getBool('SHOW_INVOICE_CREATION_SELECTION', true));
                setAllowPriceEditProduct(getBool('ALLOW_PRICE_EDIT_PRODUCT_ITEMS', false));
                setAllowPriceEditService(getBool('ALLOW_PRICE_EDIT_SERVICE_ITEMS', false));
                setAllowSalesCorrection(getBool('ALLOW_SALES_CORRECTION', false));
                const roundOffSetting = settings.find(s => s.typeName === 'ROUND_OFF_MODE');
                setRoundOffMode(roundOffSetting?.value ? String(roundOffSetting.value) : 'CEILING');
            }).catch(() => {});
        }
    }, [id]);

    // Load existing data for edit mode
    useEffect(() => {
        if (id) {
            setIsLoading(true);
            genericGetOne(ENTITY_ID_CUSTOMER_SALE, id)
                .then(async ({ data }) => {
                    // Store original data to preserve id, orgId, and other base fields
                    setOriginalData(data);

                    // Transform data for form - convert vehicleNumber string to vehicle object
                    const transformedData = {
                        ...data,
                        // Create vehicle object from vehicleNumber for display
                        // Note: Don't set 'id' here because data.vehicleNumber is the registration number,
                        // not the MongoDB document ID. Setting id would cause AGCSelect to try fetching
                        // a vehicle using the registration number as the document ID.
                        vehicle: data.vehicleNumber ? {
                            name: data.vehicleNumber,
                            label: data.vehicleNumber,
                            vehicleNumber: data.vehicleNumber
                        } : null,
                        // Ensure salesCustomer is properly formatted
                        salesCustomer: data.salesCustomer ? {
                            id: data.salesCustomer.id,
                            name: data.salesCustomer.name,
                            value: data.salesCustomer.id,
                            label: data.salesCustomer.name
                        } : null,
                        // Ensure salesPersonId is properly formatted
                        salesPersonId: data.salesPersonId ? {
                            id: data.salesPersonId,
                            value: data.salesPersonId
                        } : (currentUser ? {
                            id: currentUser.id || currentUser._id,
                            name: currentUser.name || currentUser.fullName || currentUser.userName || currentUser.email
                        } : null)
                    };

                    // If this is a partial record (from Step 1), show preview ticket number (not committed yet)
                    if (data.isPartialRecord === true && !data.ticketSequenceNumber) {
                        try {
                            const billResponse = await getCurrentTicketNumber();
                            const currentNumber = billResponse?.data?.currentNumber || 0;
                            const previewNumber = String(currentNumber + 1).padStart(4, '0');
                            transformedData.ticketSequenceNumber = previewNumber;
                            setIsTicketPreview(true);
                        } catch (error) {
                            console.error("Error fetching preview ticket number for partial record:", error);
                        }
                    }

                    setFormData(transformedData);
                    setIsLoading(false);
                })
                .catch((error) => {
                    showToast("Error loading sale: " + error.message, TOAST_FAILURE);
                    setIsLoading(false);
                    navigate('/sales-list');
                });
        } else {
            // For new entries, set current user as sales person and show preview ticket number
            const initializeNewSale = async () => {
                try {
                    // Fetch current ticket number and org settings in parallel
                    const [billResponse, settingsResponse] = await Promise.all([
                        getCurrentTicketNumber(),
                        getAllOrgSettings().catch(() => ({ data: [] }))
                    ]);
                    const currentNumber = billResponse?.data?.currentNumber || 0;
                    const previewNumber = String(currentNumber + 1).padStart(4, '0');

                    // Apply org settings
                    const settings = settingsResponse?.data || [];
                    const getBoolSetting = (name, defaultVal) => {
                        const s = settings.find(s => s.typeName === name);
                        return s ? (s.value === true || s.value === 'true') : defaultVal;
                    };
                    const createInvoiceDefault = getBoolSetting('CREATE_INVOICE_BY_DEFAULT', false);
                    setWeighbridgeEnabled(getBoolSetting('ENABLE_WEIGHBRIDGE_CAPTURE', true));
                    setCameraEnabled(getBoolSetting('ENABLE_CAMERA_CAPTURE', true));
                    setShowInvoiceCreationSelection(getBoolSetting('SHOW_INVOICE_CREATION_SELECTION', true));
                    setAllowPriceEditProduct(getBoolSetting('ALLOW_PRICE_EDIT_PRODUCT_ITEMS', false));
                    setAllowPriceEditService(getBoolSetting('ALLOW_PRICE_EDIT_SERVICE_ITEMS', false));
                    setAllowSalesCorrection(getBoolSetting('ALLOW_SALES_CORRECTION', false));
                    const roundOffSetting = settings.find(s => s.typeName === 'ROUND_OFF_MODE');
                    setRoundOffMode(roundOffSetting?.value ? String(roundOffSetting.value) : 'CEILING');

                    setFormData(prev => ({
                        ...prev,
                        ticketSequenceNumber: previewNumber,
                        createInvoice: createInvoiceDefault,
                        salesPersonId: currentUser ? {
                            id: currentUser.id || currentUser._id,
                            name: currentUser.name || currentUser.fullName || currentUser.userName || currentUser.email
                        } : null
                    }));
                    setIsTicketPreview(true);
                } catch (error) {
                    console.error("Error fetching preview ticket number:", error);
                    // Still set the sales person even if ticket number fetch fails
                    if (currentUser) {
                        setFormData(prev => ({
                            ...prev,
                            salesPersonId: {
                                id: currentUser.id || currentUser._id,
                                name: currentUser.name || currentUser.fullName || currentUser.userName || currentUser.email
                            }
                        }));
                    }
                }
            };

            initializeNewSale();

            // Focus on vehicle number for new entries
            setTimeout(() => {
                if (vehicleNumberRef.current) {
                    vehicleNumberRef.current.focus();
                }
            }, 100);
        }
    }, [id, navigate, currentUser]);

    // Load the organization record (refreshed from the server when reachable):
    // gates the e-Way bill feature and feeds the readiness checks (GSTIN / pin code).
    useEffect(() => {
        getOrgInfo()
            .then(({ data }) => {
                setOrgRecord(data || null);
                const enabled = !!data?.gstServicesEnabled;
                setGstEnabled(enabled);
                localStorage.setItem("gstServicesEnabled", String(enabled));
            })
            .catch(() => setGstEnabled((prev) => (prev === null ? false : prev)));
    }, []);

    // e-Invoicing is an opt-in inside the GST Services add-on, and the portal rejects
    // e-invoices for customers with no GSTIN — so both gates are checked before the switch
    // is offered at all.
    const einvoiceEnabled = gstEnabled === true && !!orgRecord?.einvoiceEnabled;
    const customerHasGstin = !!(ewayCustomer?.gstNumber || "").trim();

    // Switching to a customer with no GSTIN makes an e-Invoice impossible — clear the
    // request rather than letting it fail at the portal after the sale is saved.
    useEffect(() => {
        if (einvoiceEnabled && !customerHasGstin && formik.values.generateEinvoice) {
            formik.setFieldValue('generateEinvoice', false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [einvoiceEnabled, customerHasGstin]);

    // Load the full customer record whenever the selected customer changes,
    // so the e-Way readiness panel can check GSTIN / pin code / address.
    const selectedCustomerId = formik.values.salesCustomer?.id || formik.values.salesCustomer?.value || null;
    useEffect(() => {
        if (!gstEnabled || !selectedCustomerId) {
            setEwayCustomer(null);
            return;
        }
        genericGetOne(ENTITY_ID_SALES_CUSTOMER, selectedCustomerId)
            .then(({ data }) => setEwayCustomer(data))
            .catch(() => setEwayCustomer(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomerId, gstEnabled]);

    // A ship-to address belongs to one customer — drop the selection when the
    // operator switches to a different customer (but not on the initial load).
    const previousShipToCustomerRef = useRef(null);
    useEffect(() => {
        const previousCustomerId = previousShipToCustomerRef.current;
        previousShipToCustomerRef.current = selectedCustomerId;
        if (previousCustomerId && selectedCustomerId !== previousCustomerId) {
            formik.setFieldValue('shipToAddressId', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomerId]);

    // The shipping address object behind the current selection (null = billing address)
    const selectedShipTo = (formik.values.shipToAddressId
        && (ewayCustomer?.shippingAddresses || []).find((a) => a.id === formik.values.shipToAddressId)) || null;

    // A sale requesting an e-Way bill may only be saved when the readiness check
    // has no failing issues — the paid generation would be rejected otherwise.
    const ewayBlocked = gstEnabled === true
        && formik.values.createInvoice === true
        && formik.values.generateEwayBill === true
        && !originalData?.ewayBillStatus
        && buildEwayChecks({
            org: orgRecord,
            customer: ewayCustomer,
            values: formik.values,
            ticketNumber: formik.values.ticketSequenceNumber,
            shipTo: selectedShipTo,
        }).some((c) => c.level === "fail");
    ewayBlockedRef.current = ewayBlocked;

    // Load all TaxPlans on mount for tax calculations
    useEffect(() => {
        genericGetAll(ENTITY_ID_TAX_PLAN, { pageNumber: 1, pageSize: 1000 })
            .then((response) => {
                // Create lookup maps: by ID and by name
                const lookup = {};
                // Response format is {totalCount, entities} or {data: {totalCount, entities}}
                const responseData = response?.data || response;
                const taxPlans = responseData?.entities || responseData?.data || [];

                if (Array.isArray(taxPlans)) {
                    taxPlans.forEach(taxPlan => {
                        // Lookup by ID
                        if (taxPlan.id) {
                            lookup[taxPlan.id] = taxPlan;
                        }
                        // Also lookup by name (fallback)
                        if (taxPlan.name) {
                            lookup[taxPlan.name] = taxPlan;
                        }
                    });
                }
                setTaxPlansLookup(lookup);
            })
            .catch((error) => {
                console.error("Error loading tax plans:", error);
            });
    }, []);

    // Load users list for sales person lookup
    useEffect(() => {
        genericGetAll(ENTITY_ID_USER, { pageNumber: 1, pageSize: 1000 })
            .then((response) => {
                const responseData = response?.data || response;
                const users = responseData?.entities || responseData?.data || [];
                setUsersList(users);
            })
            .catch((error) => {
                console.error("Error loading users:", error);
            });
    }, []);

    // Update salesPersonId with name once users list is loaded (for edit mode)
    useEffect(() => {
        if (usersList.length > 0 && formData.salesPersonId && !formData.salesPersonId.name) {
            const salesPersonId = formData.salesPersonId.id || formData.salesPersonId;
            const user = usersList.find(u => u.id === salesPersonId);
            if (user) {
                setFormData(prev => ({
                    ...prev,
                    salesPersonId: {
                        id: user.id,
                        name: user.name || user.fullName || user.userName || user.email,
                        value: user.id,
                        label: user.name || user.fullName || user.userName || user.email
                    }
                }));
            }
        }
    }, [usersList, formData.salesPersonId]);

    // Load captured images (both edit and view modes)
    useEffect(() => {
        if (originalData?.id) {
            setImagesLoading(true);
            getImagesForEntry('SALE', originalData.id)
                .then((response) => {
                    const data = response?.data;
                    if (data?.success && data?.images) {
                        setSavedImages(data.images);
                    }
                })
                .catch((error) => {
                    console.error("Error loading images:", error);
                })
                .finally(() => {
                    setImagesLoading(false);
                });
        }
    }, [originalData?.id]);

    // Fetch vehicle's empty weight when a vehicle is selected or changed
    useEffect(() => {
        const vehicleId = formik.values.vehicle?.id;
        const previousVehicleId = previousVehicleIdRef.current;

        // Check if vehicle actually changed (user selected a different vehicle)
        const vehicleUserChanged = previousVehicleId !== null && vehicleId && vehicleId !== previousVehicleId;

        // Check if this is first selection in new mode (no previous vehicle, no empty weight)
        const isFirstSelectionInNewMode = previousVehicleId === null && vehicleId && !formik.values.emptyWeight;

        // Update the ref with current vehicle ID
        previousVehicleIdRef.current = vehicleId;

        // Fetch vehicle empty weight if:
        // 1. User changed the vehicle (vehicleUserChanged), OR
        // 2. First vehicle selection in new sale mode (isFirstSelectionInNewMode)
        if (vehicleUserChanged || isFirstSelectionInNewMode) {
            genericGetOne(ENTITY_ID_VEHICLE, vehicleId)
                .then(({ data }) => {
                    if (data && data.emptyWeight) {
                        const weight = parseFloat(data.emptyWeight);
                        if (!isNaN(weight) && weight > 0) {
                            formik.setFieldValue('emptyWeight', weight);
                        }
                    }
                })
                .catch((error) => {
                    console.error("Error fetching vehicle empty weight:", error);
                });
        }
    }, [formik.values.vehicle?.id]);

    // Handle items list changes
    const handleItemsChange = useCallback((items) => {
        formik.setFieldValue('itemsList', items);
    }, [formik]);

    // Handle payments list changes
    const handlePaymentsChange = useCallback((payments) => {
        formik.setFieldValue('paymentsList', payments);
    }, [formik]);

    // Helper function to calculate balance due
    const calculateBalanceDue = useCallback((values) => {
        const parseNumber = (value) => {
            if (value === null || value === undefined || value === '') return 0;
            const parsed = parseFloat(value);
            return isNaN(parsed) ? 0 : parsed;
        };

        const getTaxPercentage = (tax) => {
            let percentage = tax.percentage || tax.taxPercentage;
            if ((percentage === null || percentage === undefined) && tax.id && taxPlansLookup[tax.id]) {
                percentage = taxPlansLookup[tax.id].taxPercentage;
            }
            if ((percentage === null || percentage === undefined) && tax.name && taxPlansLookup[tax.name]) {
                percentage = taxPlansLookup[tax.name].taxPercentage;
            }
            return parseNumber(percentage);
        };

        const items = values.itemsList || [];
        let totalAfterTax = 0;

        items.forEach(item => {
            const price = parseNumber(item.price);
            const quantity = parseNumber(item.quantity);
            const itemDiscount = parseNumber(item.discountAmount);
            const subtotal = price * quantity;
            const afterDiscount = subtotal - itemDiscount;

            let taxAmount = 0;
            if (values.createInvoice && item.taxPlans && item.taxPlans.length > 0) {
                item.taxPlans.forEach(tax => {
                    const taxPercent = getTaxPercentage(tax);
                    taxAmount += (afterDiscount * taxPercent) / 100;
                });
            }
            totalAfterTax += afterDiscount + taxAmount;
        });

        const autoRoundOff = computeRoundOffAmount(totalAfterTax, roundOffMode);
        const grandTotal = totalAfterTax + autoRoundOff;

        const payments = values.paymentsList || [];
        const totalPayments = payments.reduce((sum, p) => sum + parseNumber(p.totalAmount), 0);

        return {
            grandTotal,
            totalPayments,
            balanceDue: grandTotal - totalPayments,
            hasPayments: payments.length > 0
        };
    }, [taxPlansLookup, roundOffMode]);

    // Handle form submission with validation and payment warning check
    const handleSubmitWithWarning = useCallback((values) => {
        // First, check for required field validations
        const errors = [];

        if (!values.vehicle) {
            errors.push("Please select a Vehicle");
        }

        if (!values.salesCustomer) {
            errors.push("Please select a Customer");
        }

        // Check if at least one inventory item is added
        const inventoryItems = (values.itemsList || []).filter(item => item.itemType === 'INVENTORY');
        if (inventoryItems.length === 0) {
            errors.push("Please add at least one Inventory Item");
        }

        // Determine if item is weight-based for validation
        const invItem = inventoryItems.length > 0 ? inventoryItems[0] : null;
        const isWeightBased = !invItem || !invItem.unitSymbol || invItem.unitSymbol.toLowerCase() === 'ton';

        if (isWeightBased) {
            if (!values.loadedWeight || parseFloat(values.loadedWeight) <= 0) {
                errors.push("Please enter the Loaded Weight");
            }

            if (!values.emptyWeight || parseFloat(values.emptyWeight) <= 0) {
                errors.push("Please enter the Empty Weight");
            }

            if (values.loadedWeight && values.emptyWeight && parseFloat(values.loadedWeight) <= parseFloat(values.emptyWeight)) {
                errors.push("Loaded Weight must be greater than Empty Weight");
            }
        } else {
            // For non-weight items, validate that quantity is entered
            if (invItem && (!invItem.quantity || parseFloat(invItem.quantity) <= 0)) {
                errors.push("Please enter a valid Quantity for the inventory item");
            }
        }

        // If there are validation errors, show error dialog and prevent saving
        if (errors.length > 0) {
            setValidationErrors(errors);
            setShowValidationError(true);
            return;
        }

        // Validation passed, now check payment status
        const { balanceDue, hasPayments, grandTotal } = calculateBalanceDue(values);

        // Format currency for the warning message
        const formatCurrency = (amount) => {
            return new Intl.NumberFormat('en-IN', {
                style: 'currency',
                currency: 'INR',
                minimumFractionDigits: 2
            }).format(amount);
        };

        if (!hasPayments) {
            setPaymentWarningMessage(`No payments have been added. The full amount of ${formatCurrency(grandTotal)} will be recorded as due. Do you want to proceed?`);
            setShowPaymentWarning(true);
            return;
        }

        if (balanceDue > 0) {
            setPaymentWarningMessage(`There is a balance due of ${formatCurrency(balanceDue)}. Do you want to proceed with the sale?`);
            setShowPaymentWarning(true);
            return;
        }

        // No warning needed, submit directly
        formik.submitForm();
    }, [calculateBalanceDue, formik]);

    // Confirm and proceed with submission
    const handleConfirmSubmit = useCallback(() => {
        setShowPaymentWarning(false);
        formik.submitForm();
    }, [formik]);

    // Parse number safely (component-level, used by net-weight + correction calc)
    const parseNumber = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Calculate net weight with optional loaded-weight correction.
    // Sales correction is ADDED to the loaded weight (upward adjustment),
    // unlike purchase where correction is subtracted as wastage.
    const correctionAmount = parseNumber(formik.values.loadedWeightCorrection);
    const effectiveLoaded = parseNumber(formik.values.loadedWeight) + correctionAmount;
    const netWeight = effectiveLoaded - parseNumber(formik.values.emptyWeight);

    // Determine if the selected inventory item is weight-based (Ton) or non-weight (e.g., Cft)
    const isWeightBasedItem = (() => {
        const invItem = (formik.values.itemsList || []).find(item => item.itemType === 'INVENTORY');
        if (!invItem || !invItem.unitSymbol) return true; // Default to weight-based
        return invItem.unitSymbol.toLowerCase() === 'ton';
    })();

    // Handle cancel
    const handleCancel = () => {
        navigate('/sales-list');
    };

    // Handle vehicle data capture (weight and image)
    // Always capture to memory only — image is saved when sale is created/updated
    // For non-weight items (e.g., Cft), only capture image, skip weight
    // Respects weighbridgeEnabled and cameraEnabled org settings
    const handleCapture = async () => {
        const wantWeight = isWeightBasedItem && weighbridgeEnabled;
        const wantImage = cameraEnabled;
        if (!wantWeight && !wantImage) return;

        const res = await capture({ weight: wantWeight, image: wantImage });
        if (res.cancelled) return;

        if (res.weight != null) {
            formik.setFieldValue('loadedWeight', res.weight);
            showToast("Weight captured: " + res.weight, TOAST_SUCCESS);
        } else if (wantWeight) {
            showToast(res.weightError?.message || "Failed to capture weight", TOAST_FAILURE);
        }

        if (res.image) {
            setCapturedLoadedImage({
                url: `data:${res.image.contentType};base64,${res.image.imageData}`,
                base64Image: res.image.imageData,
                contentType: res.image.contentType,
                timestamp: res.image.timestamp,
                saved: false
            });
            if (!wantWeight) {
                showToast("Image captured successfully", TOAST_SUCCESS);
            }
        } else if (wantImage) {
            showToast(res.imageError?.message || "Failed to capture image", TOAST_FAILURE);
        }
    };

    // Keyboard shortcuts for efficiency
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Ctrl+S to save (only when not in read-only mode)
            if (e.ctrlKey && e.key === 's' && !readOnly) {
                e.preventDefault();
                handleSubmitWithWarning(formik.values);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [formik, handleSubmitWithWarning, readOnly]);

    if (isLoading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                <Spinner animation="border" variant="primary" />
                <span className="ms-2">Loading sale data...</span>
            </div>
        );
    }

    return (
        <>
        <Form onSubmit={formik.handleSubmit}>
            <div className="container-fluid px-0">
                <Row>
                    {/* LEFT COLUMN - Primary Sale Data */}
                    <Col lg={8}>
                        {/* Combined Sale & Vehicle Card */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            {readOnly && (
                                <div className="px-4 py-2" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                    <div className="d-flex align-items-center">
                                        <i className="bi bi-receipt me-2" style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>SALE INFORMATION</span>
                                    </div>
                                </div>
                            )}
                            <Card.Body className="p-4">
                                {/* Sale Info Row */}
                                <Row className="mb-3">
                                    <Col md={4}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted small">Ticket No.</label>
                                            <div className="fw-semibold fs-5 d-flex align-items-center gap-2">
                                                {formik.values.ticketSequenceNumber || (readOnly ? 'N/A' : 'Loading...')}
                                                {!readOnly && isTicketPreview && (
                                                    <OverlayTrigger
                                                        trigger={['hover', 'focus', 'click']}
                                                        placement="right"
                                                        rootClose
                                                        overlay={
                                                            <Popover style={{ maxWidth: '300px' }}>
                                                                <Popover.Header as="div" className="d-flex align-items-center gap-2" style={{ background: '#f0f4ff', borderBottom: '2px solid #4a90a4' }}>
                                                                    <i className="bi bi-info-circle-fill" style={{ color: '#4a90a4' }}></i>
                                                                    <strong style={{ color: '#2c3e50' }}>Preview Ticket Number</strong>
                                                                </Popover.Header>
                                                                <Popover.Body style={{ fontSize: '13px', lineHeight: '1.5' }}>
                                                                    <p className="mb-2">This is a <strong>preview</strong> of the next ticket number. The final number will be assigned when you <strong>save</strong> the sale.</p>
                                                                    <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>
                                                                        <i className="bi bi-people-fill me-1"></i>
                                                                        If multiple sales are being created at the same time, the final number may differ slightly.
                                                                    </p>
                                                                </Popover.Body>
                                                            </Popover>
                                                        }
                                                    >
                                                        <span
                                                            tabIndex={0}
                                                            style={{ cursor: 'pointer', color: '#4a90a4', fontSize: '14px' }}
                                                        >
                                                            <i className="bi bi-info-circle-fill"></i>
                                                        </span>
                                                    </OverlayTrigger>
                                                )}
                                            </div>
                                            {!readOnly && (
                                                <small className="text-muted">{isTicketPreview ? 'Preview — assigned on save' : 'Auto-generated'}</small>
                                            )}
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        {readOnly ? (
                                            <div className="mb-3">
                                                <label className="form-label text-muted small">Customer</label>
                                                <div className="fw-semibold">{formik.values.salesCustomer?.label || formik.values.salesCustomer?.name || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCDynamicSelect
                                                    entityId={ENTITY_ID_SALES_CUSTOMER}
                                                    formik={formik}
                                                    name="Customer"
                                                    id="salesCustomer"
                                                    value={formik.values.salesCustomer}
                                                    toolTip="Select customer"
                                                    required={true}
                                                    searchField="name"
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={4}>
                                        {readOnly || (id && originalData?.isPartialRecord) ? (
                                            <div className="mb-3">
                                                <label className="form-label text-muted small">Vehicle</label>
                                                <div className="fw-semibold">{formik.values.vehicle?.label || formik.values.vehicle?.vehicleNumber || formik.values.vehicleNumber || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCDynamicSelect
                                                    entityId={ENTITY_ID_VEHICLE}
                                                    formik={formik}
                                                    name="Vehicle"
                                                    id="vehicle"
                                                    value={formik.values.vehicle}
                                                    toolTip="Select vehicle"
                                                    required={true}
                                                    searchField="vehicleNumber"
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                </Row>
                                {readOnly && (
                                    <Row className="mb-0">
                                        <Col md={4}>
                                            <div className="mb-3">
                                                <label className="form-label text-muted small">Sale Date</label>
                                                <div className="fw-semibold">{formatDateTime(formik.values.salesSecondStepDate)}</div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="mb-3">
                                                {/* The weigh-in stamp as its own labelled field - the Sale Date
                                                    beside it is salesSecondStepDate for both sale types. */}
                                                <label className="form-label text-muted small">Weigh-In Date</label>
                                                <div className="fw-semibold">{formatDateTime(formik.values.salesFirstStepDate)}</div>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="mb-3">
                                                <label className="form-label text-muted small">Sale Type</label>
                                                <div className="fw-semibold">
                                                    {originalData?.saleType === 'TWO_STEP' ? '2-Step' : '1-Step'}
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Items Section - Combined Inventory & Services */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <Card.Body className="p-0">
                                <SaleItemsSection
                                    items={formik.values.itemsList || []}
                                    onChange={handleItemsChange}
                                    defaultQuantity={netWeight !== 0 ? parseFloat(Math.abs(netWeight).toFixed(3)) : 1}
                                    taxPlansLookup={taxPlansLookup}
                                    showInventory={true}
                                    showServices={true}
                                    readOnly={readOnly}
                                    customerId={formik.values.salesCustomer?.id || formik.values.salesCustomer?.value || null}
                                    isWeightBased={isWeightBasedItem}
                                    createInvoice={formik.values.createInvoice || false}
                                    allowPriceEditProduct={allowPriceEditProduct}
                                    allowPriceEditService={allowPriceEditService}
                                />
                            </Card.Body>
                        </Card>

                        {/* Payments Section */}
                        <div className="mb-3">
                            <SalePaymentsSection
                                payments={formik.values.paymentsList || []}
                                onChange={handlePaymentsChange}
                                readOnly={readOnly}
                                balanceDueInfo={calculateBalanceDue(formik.values)}
                            />
                        </div>

                        {/* Bill Summary */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="px-4 py-2" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-calculator me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>BILL SUMMARY</span>
                                </div>
                            </div>
                            <Card.Body className="p-3">
                                <BillCalculations
                                    items={formik.values.itemsList || []}
                                    formik={formik}
                                    taxPlansLookup={taxPlansLookup}
                                    payments={formik.values.paymentsList || []}
                                    readOnly={readOnly}
                                    createInvoice={formik.values.createInvoice || false}
                                    roundOffMode={roundOffMode}
                                />
                            </Card.Body>
                        </Card>

                        {/* E-Way bill: outcome card once a bill exists, readiness check while drafting,
                            upsell for orgs without GST Services */}
                        {gstEnabled === false && !readOnly && <EwayUpsellCard />}
                        {gstEnabled === true && originalData?.ewayBillStatus ? (
                            <EwayBillOutcomeCard sale={originalData} onOpenDetails={() => setEwayDetailOpen(true)} />
                        ) : (gstEnabled === true && formik.values.createInvoice && formik.values.generateEwayBill && (
                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <div className="d-flex align-items-center px-4 py-2" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#eeecff' }}>
                                    <i className="bi bi-truck me-2" style={{ color: '#635BFF' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#635BFF' }}>E-WAY BILL</span>
                                </div>
                                <Card.Body>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <EwayBillReadinessPanel
                                                org={orgRecord}
                                                customer={ewayCustomer}
                                                values={formik.values}
                                                ticketNumber={formik.values.ticketSequenceNumber}
                                                shipTo={selectedShipTo}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            {/* Bill To / Ship To: pick which of the customer's shipping addresses the load goes to */}
                                            {formik.values.salesCustomer && (
                                                <ShipToAddressSelect
                                                    customer={ewayCustomer}
                                                    value={formik.values.shipToAddressId || ""}
                                                    onChange={(addressId) => formik.setFieldValue('shipToAddressId', addressId)}
                                                    onCustomerRefresh={setEwayCustomer}
                                                    readOnly={readOnly}
                                                />
                                            )}
                                            {/* Predict locally how the server will file the e-Way bill on sync
                                                (Regular vs Bill To / Ship To + URP) so there are no surprises. */}
                                            {formik.values.salesCustomer && ewayCustomer && (
                                                <EwayDecisionsPreview
                                                    gstNumber={ewayCustomer.gstNumber}
                                                    selectedShipTo={selectedShipTo}
                                                    hasShipTo={!!selectedShipTo}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        ))}
                    </Col>

                    {/* RIGHT COLUMN - Additional Info & Actions */}
                    <Col lg={4}>
                        {/* Vehicle Images / Weight Panel OR Quantity Entry Panel */}
                        {isWeightBasedItem ? (
                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <div className="d-flex align-items-center justify-content-between px-4 py-2" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                    <div className="d-flex align-items-center">
                                        <i className={`bi ${readOnly ? 'bi-speedometer' : weighbridgeEnabled ? 'bi-camera' : 'bi-pencil-square'} me-2`} style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>
                                            {readOnly ? 'WEIGHT INFORMATION' : weighbridgeEnabled ? 'VEHICLE IMAGES' : 'WEIGHT & IMAGES'}
                                        </span>
                                    </div>
                                    {!readOnly && (
                                        <span className="badge text-white" style={{ fontSize: '0.65rem', backgroundColor: weighbridgeEnabled ? '#4a90a4' : '#f59e0b' }}>
                                            {weighbridgeEnabled ? 'AUTO CAPTURE' : 'MANUAL ENTRY'}
                                        </span>
                                    )}
                                </div>
                                <Card.Body className="p-3">
                                    {/* Image thumbnails - show Empty only for TWO_STEP sales */}
                                    {cameraEnabled ? (() => {
                                        const isTwoStep = originalData?.saleType === 'TWO_STEP';
                                        const emptyImgSrc = savedImages.find(img => img.imageType === 'EMPTY_WEIGHT')
                                            ? `${API_URL}/api/vehicle-capture/image/${savedImages.find(img => img.imageType === 'EMPTY_WEIGHT').primaryId}`
                                            : null;
                                        const loadedImgSrc = capturedLoadedImage?.url
                                            || (savedImages.find(img => img.imageType === 'LOADED_WEIGHT')
                                                ? `${API_URL}/api/vehicle-capture/image/${savedImages.find(img => img.imageType === 'LOADED_WEIGHT').primaryId}`
                                                : null);
                                        return (
                                            <Row className="g-2 mb-3">
                                                {isTwoStep && (
                                                    <Col xs={6}>
                                                        <ImageThumbnail src={emptyImgSrc} label="Empty" color="#0d6efd" />
                                                    </Col>
                                                )}
                                                <Col xs={isTwoStep ? 6 : 12}>
                                                    <ImageThumbnail src={loadedImgSrc} label="Loaded" color="#198754" placeholder={!readOnly} />
                                                </Col>
                                            </Row>
                                        );
                                    })() : !readOnly && (
                                        <div className="text-center p-3 mb-3 rounded" style={{ backgroundColor: '#fff3cd', border: '1px dashed #ffc107' }}>
                                            <i className="bi bi-camera-video-off text-warning" style={{ fontSize: '1.5rem' }}></i>
                                            <div className="text-muted small mt-1">Camera capture is disabled</div>
                                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>Vehicle images are not required for this sale</div>
                                        </div>
                                    )}

                                    {/* Weight section - Manual inputs when weighbridge disabled, display when enabled */}
                                    {!readOnly && !weighbridgeEnabled ? (
                                        <>
                                            <Row className="g-2">
                                                <Col xs={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small text-muted mb-1">Empty Weight (Ton)</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="number"
                                                            step="0.001"
                                                            min="0"
                                                            placeholder="0.000"
                                                            value={formik.values.emptyWeight || ''}
                                                            onChange={(e) => formik.setFieldValue('emptyWeight', e.target.value ? parseFloat(e.target.value) : null)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={6}>
                                                    <Form.Group>
                                                        <Form.Label className="small text-muted mb-1">Loaded Weight (Ton)</Form.Label>
                                                        <Form.Control
                                                            size="sm"
                                                            type="number"
                                                            step="0.001"
                                                            min="0"
                                                            placeholder="0.000"
                                                            value={formik.values.loadedWeight || ''}
                                                            onChange={(e) => formik.setFieldValue('loadedWeight', e.target.value ? parseFloat(e.target.value) : null)}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </>
                                    ) : (
                                        <Row className="g-2">
                                            <Col xs={6}>
                                                <div className="text-center p-2 rounded" style={{ backgroundColor: '#f0f4ff', border: '1px solid #d6e0f0' }}>
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Empty Weight</small>
                                                    <span className="fw-bold" style={{ fontSize: '1.1rem', color: formik.values.emptyWeight ? '#0d6efd' : '#6c757d' }}>
                                                        {formik.values.emptyWeight ? `${formik.values.emptyWeight} Ton` : '-- Ton'}
                                                    </span>
                                                </div>
                                            </Col>
                                            <Col xs={6}>
                                                <div className="text-center p-2 rounded" style={{ backgroundColor: '#f0f4ff', border: '1px solid #d6e0f0' }}>
                                                    <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Loaded Weight</small>
                                                    <span className="fw-bold" style={{ fontSize: '1.1rem', color: formik.values.loadedWeight ? '#0d6efd' : '#6c757d' }}>
                                                        {formik.values.loadedWeight ? `${formik.values.loadedWeight} Ton` : '-- Ton'}
                                                    </span>
                                                </div>
                                            </Col>
                                        </Row>
                                    )}

                                    {/* Weight Correction — editable in form mode, read-only in view mode.
                                        Gated by ALLOW_SALES_CORRECTION; in read-only view we still surface
                                        existing non-zero corrections from historical sales so the data
                                        doesn't disappear if the setting is later turned off. */}
                                    {parseNumber(formik.values.loadedWeight) > 0 && (allowSalesCorrection || (readOnly && correctionAmount > 0)) && (
                                        <div className="mt-2 p-2 rounded" style={{ backgroundColor: '#fff', border: '1px solid #dee2e6' }}>
                                            <div className="d-flex align-items-center justify-content-between">
                                                <small className="text-muted">
                                                    <i className="bi bi-plus-circle me-1"></i>Weight Correction
                                                </small>
                                                {readOnly ? (
                                                    <span className="fw-semibold" style={{ fontSize: '0.95rem', color: correctionAmount > 0 ? '#dc3545' : '#6c757d' }}>
                                                        {correctionAmount > 0 ? `${correctionAmount.toFixed(3)} Ton` : '0 Ton'}
                                                    </span>
                                                ) : (
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Form.Control
                                                            type="number"
                                                            step="0.001"
                                                            min="0"
                                                            placeholder="0"
                                                            value={formik.values.loadedWeightCorrection || ''}
                                                            onChange={(e) => {
                                                                const parsed = e.target.value ? parseFloat(e.target.value) : 0;
                                                                formik.setFieldValue('loadedWeightCorrection', Number.isFinite(parsed) && parsed > 0 ? parsed : 0);
                                                            }}
                                                            onKeyDown={(e) => { if (e.key === '-' || e.key === 'e' || e.key === 'E') e.preventDefault(); }}
                                                            style={{ width: '90px', textAlign: 'right' }}
                                                            size="sm"
                                                        />
                                                        <small className="text-muted">Ton</small>
                                                    </div>
                                                )}
                                            </div>
                                            {correctionAmount > 0 && (
                                                <div className="text-end mt-1">
                                                    <small className="text-success fw-semibold">
                                                        Effective Loaded: {effectiveLoaded.toFixed(3)} Ton
                                                    </small>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Net Weight Display */}
                                    <div className="mt-3 p-2 rounded text-center" style={{ backgroundColor: netWeight > 0 ? '#d1e7dd' : netWeight < 0 ? '#f8d7da' : '#f8f9fa', border: netWeight > 0 ? '1px solid #badbcc' : netWeight < 0 ? '1px solid #f5c2c7' : '1px solid #dee2e6' }}>
                                        <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>Net Weight</small>
                                        <span className="fw-bold" style={{ fontSize: '1.4rem', color: netWeight > 0 ? '#198754' : netWeight < 0 ? '#dc3545' : '#6c757d' }}>
                                            {formik.values.loadedWeight && formik.values.emptyWeight ? `${Math.abs(netWeight).toFixed(3)} Ton` : '-- Ton'}
                                        </span>
                                    </div>

                                    {!readOnly && (weighbridgeEnabled || cameraEnabled) && (
                                        <>
                                            <div className="d-grid mt-3">
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="text-primary"
                                                    style={{
                                                        border: '1px solid #86b7fe',
                                                        backgroundColor: '#f8f9fa'
                                                    }}
                                                    onClick={handleCapture}
                                                    disabled={isCapturing}
                                                >
                                                    {isCapturing ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Capturing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-camera me-2"></i>
                                                            {weighbridgeEnabled && cameraEnabled ? 'Capture' : cameraEnabled ? 'Capture Image' : 'Capture Weight'}
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                            <div className="text-center mt-2">
                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                    <i className="bi bi-info-circle me-1"></i>
                                                    {weighbridgeEnabled && cameraEnabled
                                                        ? 'Weight & images captured from weighing machine'
                                                        : cameraEnabled
                                                            ? 'Capture vehicle image (weight entered manually)'
                                                            : 'Capture weight from weighing machine'}
                                                </small>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        ) : (
                            /* Quantity & Image Panel for non-weight items (e.g., Cft) */
                            <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                                <div className="d-flex align-items-center justify-content-between px-4 py-2" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                    <div className="d-flex align-items-center">
                                        <i className={`bi ${readOnly ? 'bi-box-seam' : cameraEnabled ? 'bi-camera' : 'bi-box-seam'} me-2`} style={{ color: '#4a90a4' }}></i>
                                        <span className="fw-semibold small" style={{ color: '#4a90a4' }}>
                                            {readOnly ? 'QUANTITY & IMAGE' : cameraEnabled ? 'VEHICLE IMAGE' : 'QUANTITY'}
                                        </span>
                                    </div>
                                </div>
                                <Card.Body className="p-3">
                                    {/* Image thumbnail or camera-off message */}
                                    {cameraEnabled ? (() => {
                                        const loadedImgSrc = capturedLoadedImage?.url
                                            || (savedImages.find(img => img.imageType === 'LOADED_WEIGHT')
                                                ? `${API_URL}/api/vehicle-capture/image/${savedImages.find(img => img.imageType === 'LOADED_WEIGHT').primaryId}`
                                                : null);
                                        return (
                                            <Row className="g-2 mb-3">
                                                <Col xs={12}>
                                                    <ImageThumbnail src={loadedImgSrc} label="Loaded" color="#198754" placeholder={!readOnly} />
                                                </Col>
                                            </Row>
                                        );
                                    })() : !readOnly && (
                                        <div className="text-center p-3 mb-3 rounded" style={{ backgroundColor: '#fff3cd', border: '1px dashed #ffc107' }}>
                                            <i className="bi bi-camera-video-off text-warning" style={{ fontSize: '1.5rem' }}></i>
                                            <div className="text-muted small mt-1">Camera capture is disabled</div>
                                            <div className="text-muted" style={{ fontSize: '0.7rem' }}>Vehicle images are not required for this sale</div>
                                        </div>
                                    )}

                                    {/* Quantity Display */}
                                    {(() => {
                                        const invItem = (formik.values.itemsList || []).find(i => i.itemType === 'INVENTORY');
                                        const unitLabel = invItem?.unitSymbol || 'Units';
                                        return (
                                            <div className="p-2 rounded text-center" style={{
                                                backgroundColor: invItem?.quantity > 0 ? '#d1e7dd' : '#f8f9fa',
                                                border: invItem?.quantity > 0 ? '1px solid #badbcc' : '1px solid #dee2e6'
                                            }}>
                                                <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
                                                    Quantity ({unitLabel})
                                                </small>
                                                <span className="fw-bold" style={{
                                                    fontSize: '1.4rem',
                                                    color: invItem?.quantity > 0 ? '#198754' : '#6c757d'
                                                }}>
                                                    {invItem?.quantity > 0 ? `${invItem.quantity} ${unitLabel}` : `-- ${unitLabel}`}
                                                </span>
                                            </div>
                                        );
                                    })()}

                                    {!readOnly && cameraEnabled && (
                                        <>
                                            <div className="d-grid mt-3">
                                                <Button
                                                    variant="light"
                                                    size="sm"
                                                    className="text-primary"
                                                    style={{
                                                        border: '1px solid #86b7fe',
                                                        backgroundColor: '#f8f9fa'
                                                    }}
                                                    onClick={handleCapture}
                                                    disabled={isCapturing}
                                                >
                                                    {isCapturing ? (
                                                        <>
                                                            <Spinner animation="border" size="sm" className="me-2" />
                                                            Capturing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="bi bi-camera me-2"></i>
                                                            Capture Image
                                                        </>
                                                    )}
                                                </Button>
                                            </div>
                                            <div className="text-center mt-2">
                                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                                                    <i className="bi bi-info-circle me-1"></i>
                                                    Capture vehicle image & enter quantity above
                                                </small>
                                            </div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        )}

                        {/* Additional Details - Compact */}
                        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: '8px' }}>
                            <div className="px-4 py-2" style={{ borderRadius: '8px 8px 0 0', backgroundColor: '#e8f4fc' }}>
                                <div className="d-flex align-items-center">
                                    <i className="bi bi-card-checklist me-2" style={{ color: '#4a90a4' }}></i>
                                    <span className="fw-semibold small" style={{ color: '#4a90a4' }}>ADDITIONAL DETAILS</span>
                                </div>
                            </div>
                            <Card.Body className="p-3">
                                <Row className="g-2">
                                    <Col md={6}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Sales Person</label>
                                                <div className="fw-semibold small">{formik.values.salesPersonId?.label || formik.values.salesPersonId?.name || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCSelect
                                                    entityId={ENTITY_ID_USER}
                                                    formik={formik}
                                                    name="Sales Person"
                                                    id="salesPersonId"
                                                    value={formik.values.salesPersonId}
                                                    toolTip="Select sales person"
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Phone</label>
                                                <div className="fw-semibold small">{formik.values.customerPhoneNumber || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCText
                                                    formik={formik}
                                                    name="Phone"
                                                    id="customerPhoneNumber"
                                                    value={formik.values.customerPhoneNumber}
                                                    toolTip="Customer phone"
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Driver</label>
                                                <div className="fw-semibold small">{formik.values.driverName || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCText
                                                    formik={formik}
                                                    name="Driver"
                                                    id="driverName"
                                                    value={formik.values.driverName}
                                                    toolTip="Driver name"
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Batta</label>
                                                <div className="fw-semibold small">{formik.values.battaAmount ? `₹${parseFloat(formik.values.battaAmount).toFixed(2)}` : '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCText
                                                    formik={formik}
                                                    name="Batta"
                                                    id="battaAmount"
                                                    value={formik.values.battaAmount}
                                                    toolTip="Driver batta expense"
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Destination</label>
                                                <div className="fw-semibold small">{formik.values.destination || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCText
                                                    formik={formik}
                                                    name="Destination"
                                                    id="destination"
                                                    value={formik.values.destination}
                                                    toolTip="Delivery destination"
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Lead (km)</label>
                                                <div className="fw-semibold small">{formik.values.leadKm || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCText
                                                    formik={formik}
                                                    name="Lead (km)"
                                                    id="leadKm"
                                                    value={formik.values.leadKm}
                                                    toolTip="Lead distance"
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    <Col md={12}>
                                        {readOnly ? (
                                            <div className="mb-2">
                                                <label className="form-label text-muted small mb-0">Remarks</label>
                                                <div className="fw-semibold small">{formik.values.remarks || '-'}</div>
                                            </div>
                                        ) : (
                                            <Form.Group>
                                                <AGCTextArea
                                                    formik={formik}
                                                    name="Remarks"
                                                    id="remarks"
                                                    value={formik.values.remarks}
                                                    toolTip="Additional notes"
                                                    rows={2}
                                                    required={false}
                                                />
                                            </Form.Group>
                                        )}
                                    </Col>
                                    {showInvoiceCreationSelection && (
                                    <Col md={12}>
                                        <div
                                            className="d-flex align-items-center justify-content-between mt-2 px-3 py-2"
                                            style={{
                                                borderRadius: '8px',
                                                border: formik.values.createInvoice ? '2px solid #198754' : '2px dashed #ced4da',
                                                backgroundColor: formik.values.createInvoice ? '#d1e7dd' : '#f8f9fa',
                                                cursor: readOnly ? 'default' : 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => {
                                                if (readOnly) return;
                                                const next = !formik.values.createInvoice;
                                                formik.setFieldValue('createInvoice', next);
                                                if (!next) {
                                                    formik.setFieldValue('generateEwayBill', false);
                                                    formik.setFieldValue('generateEinvoice', false);
                                                }
                                            }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <Form.Check
                                                    type="switch"
                                                    id="createInvoice"
                                                    checked={formik.values.createInvoice || false}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        formik.setFieldValue('createInvoice', e.target.checked);
                                                        if (!e.target.checked) {
                                                            formik.setFieldValue('generateEwayBill', false);
                                                            formik.setFieldValue('generateEinvoice', false);
                                                        }
                                                    }}
                                                    disabled={readOnly}
                                                    className="me-2"
                                                    style={{ transform: 'scale(1.3)' }}
                                                />
                                                <div>
                                                    <span className={`fw-bold ${formik.values.createInvoice ? 'text-success' : 'text-muted'}`}>
                                                        <i className={`bi ${formik.values.createInvoice ? 'bi-receipt-cutoff' : 'bi-receipt'} me-2`}></i>
                                                        Create Invoice
                                                    </span>
                                                    <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {formik.values.createInvoice ? 'Tax will be applied to all items' : 'No tax will be charged on this sale'}
                                                    </small>
                                                </div>
                                            </div>
                                            <OverlayTrigger
                                                placement="left"
                                                overlay={
                                                    <Tooltip id="createInvoice-tooltip">
                                                        When enabled, tax will be applied to all items and included in the total calculation. When disabled, no tax will be considered — the sale will be recorded without any tax charges.
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="bi bi-info-circle text-muted" style={{ cursor: 'pointer', fontSize: '1rem' }} onClick={(e) => e.stopPropagation()}></i>
                                            </OverlayTrigger>
                                        </div>
                                    </Col>
                                    )}
                                    {/* e-Invoice: only for orgs registered for it, and only when the
                                        customer is GST-registered — the portal rejects B2C e-invoices. */}
                                    {showInvoiceCreationSelection && einvoiceEnabled && formik.values.createInvoice && (
                                    <Col md={12}>
                                        <div
                                            className="d-flex align-items-center justify-content-between mt-2 px-3 py-2"
                                            style={{
                                                borderRadius: '8px',
                                                border: formik.values.generateEinvoice ? '2px solid #0d9488' : '2px dashed #ced4da',
                                                backgroundColor: formik.values.generateEinvoice ? '#d9f5f1' : '#f8f9fa',
                                                cursor: (readOnly || !customerHasGstin) ? 'default' : 'pointer',
                                                opacity: customerHasGstin ? 1 : 0.6,
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => {
                                                if (!readOnly && customerHasGstin) {
                                                    formik.setFieldValue('generateEinvoice', !formik.values.generateEinvoice);
                                                }
                                            }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <Form.Check
                                                    type="switch"
                                                    id="generateEinvoice"
                                                    checked={formik.values.generateEinvoice || false}
                                                    onChange={(e) => { e.stopPropagation(); formik.setFieldValue('generateEinvoice', e.target.checked); }}
                                                    disabled={readOnly || !customerHasGstin}
                                                    className="me-2"
                                                    style={{ transform: 'scale(1.3)' }}
                                                />
                                                <div>
                                                    <span className={`fw-bold ${formik.values.generateEinvoice ? 'text-success' : 'text-muted'}`}>
                                                        <i className="bi bi-shield-check me-2"></i>
                                                        Generate E-Invoice (IRN)
                                                    </span>
                                                    <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {!customerHasGstin
                                                            ? 'This customer has no GSTIN — an e-Invoice is not possible'
                                                            : formik.values.generateEinvoice
                                                                ? 'This invoice will be registered with the GST portal'
                                                                : 'No e-Invoice for this sale'}
                                                    </small>
                                                </div>
                                            </div>
                                            <OverlayTrigger
                                                placement="left"
                                                overlay={
                                                    <Tooltip id="generateEinvoice-tooltip">
                                                        Registers this invoice with the GST portal to get an IRN. If the
                                                        e-Way bill switch is also on, the e-Way bill is created from that
                                                        IRN in the same request.
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="bi bi-info-circle text-muted" style={{ cursor: 'pointer', fontSize: '1rem' }} onClick={(e) => e.stopPropagation()}></i>
                                            </OverlayTrigger>
                                        </div>
                                    </Col>
                                    )}
                                    {showInvoiceCreationSelection && gstEnabled === true && formik.values.createInvoice && (
                                    <Col md={12}>
                                        <div
                                            className="d-flex align-items-center justify-content-between mt-2 px-3 py-2"
                                            style={{
                                                borderRadius: '8px',
                                                border: formik.values.generateEwayBill ? '2px solid #635BFF' : '2px dashed #ced4da',
                                                backgroundColor: formik.values.generateEwayBill ? '#e8e7ff' : '#f8f9fa',
                                                cursor: readOnly ? 'default' : 'pointer',
                                                transition: 'all 0.2s ease'
                                            }}
                                            onClick={() => { if (!readOnly) formik.setFieldValue('generateEwayBill', !formik.values.generateEwayBill); }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <Form.Check
                                                    type="switch"
                                                    id="generateEwayBill"
                                                    checked={formik.values.generateEwayBill || false}
                                                    onChange={(e) => { e.stopPropagation(); formik.setFieldValue('generateEwayBill', e.target.checked); }}
                                                    disabled={readOnly}
                                                    className="me-2"
                                                    style={{ transform: 'scale(1.3)' }}
                                                />
                                                <div>
                                                    <span className={`fw-bold ${formik.values.generateEwayBill ? 'text-primary' : 'text-muted'}`}>
                                                        <i className="bi bi-truck me-2"></i>
                                                        Generate E-Way Bill
                                                    </span>
                                                    <small className="d-block text-muted" style={{ fontSize: '0.75rem' }}>
                                                        {!formik.values.generateEwayBill
                                                            ? 'No e-Way bill for this sale'
                                                            : formik.values.generateEinvoice
                                                                ? 'Will be created from the e-Invoice above, in the same step'
                                                                : 'An e-Way bill will be requested with this invoice'}
                                                    </small>
                                                </div>
                                            </div>
                                            <OverlayTrigger
                                                placement="left"
                                                overlay={
                                                    <Tooltip id="generateEwayBill-tooltip">
                                                        Marks this sale so an e-Way bill is generated along with the invoice. Available only when Create Invoice is on.
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="bi bi-info-circle text-muted" style={{ cursor: 'pointer', fontSize: '1rem' }} onClick={(e) => e.stopPropagation()}></i>
                                            </OverlayTrigger>
                                        </div>
                                    </Col>
                                    )}
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* Action Buttons */}
                        <div className="d-grid gap-2">
                            {readOnly ? (
                                <>
                                    <Button
                                        variant="light"
                                        onClick={() => navigate(`/sale-print/${id}`)}
                                        size="lg"
                                        className="text-success"
                                        style={{ border: '1px solid #198754', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                    >
                                        <i className="bi bi-printer me-2"></i>
                                        View Print Receipt
                                    </Button>
                                    <Button
                                        variant="light"
                                        onClick={handleCancel}
                                        size="lg"
                                        className="text-primary"
                                        style={{ border: '1px solid #86b7fe', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                    >
                                        <i className="bi bi-arrow-left me-2"></i>
                                        Back to Sales List
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Button
                                        type="button"
                                        variant="light"
                                        disabled={isSaving || ewayBlocked}
                                        size="lg"
                                        className="text-primary"
                                        style={{ border: '1px solid #86b7fe', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                        onClick={() => handleSubmitWithWarning(formik.values)}
                                    >
                                        {isSaving && <Spinner animation="border" size="sm" className="me-2" />}
                                        <i className="bi bi-check-lg me-2"></i>
                                        {id ? 'Update Sale' : 'Create Sale'}
                                    </Button>
                                    {ewayBlocked && (
                                        <div className="text-danger text-center small">
                                            <i className="bi bi-x-octagon me-1"></i>
                                            Fix the E-Way Bill Check issues below the Bill Summary — or turn off
                                            Generate E-Way Bill — to save this sale.
                                        </div>
                                    )}
                                    <Button
                                        variant="light"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                        className="text-secondary"
                                        style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa', borderRadius: '8px' }}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>

            {/* Validation Error Modal */}
            <Modal
                show={showValidationError}
                onHide={() => setShowValidationError(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="bg-danger text-white">
                    <Modal.Title>
                        <i className="bi bi-x-circle me-2"></i>
                        Required Fields Missing
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted mb-3">Please fix the following errors before saving:</p>
                    <ul className="mb-0">
                        {validationErrors.map((error, index) => (
                            <li key={index} className="text-danger mb-2">
                                <i className="bi bi-exclamation-circle me-2"></i>
                                {error}
                            </li>
                        ))}
                    </ul>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="light"
                        className="text-danger"
                        style={{ border: '1px solid #dc3545', backgroundColor: '#f8f9fa' }}
                        onClick={() => setShowValidationError(false)}
                    >
                        <i className="bi bi-arrow-left me-1"></i>
                        Go Back & Fix
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Payment Warning Modal */}
            <Modal
                show={showPaymentWarning}
                onHide={() => setShowPaymentWarning(false)}
                centered
                backdrop="static"
            >
                <Modal.Header closeButton className="bg-warning-subtle">
                    <Modal.Title>
                        <i className="bi bi-exclamation-triangle text-warning me-2"></i>
                        Payment Warning
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">{paymentWarningMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="light"
                        className="text-secondary"
                        style={{ border: '1px solid #ced4da', backgroundColor: '#f8f9fa' }}
                        onClick={() => setShowPaymentWarning(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="light"
                        className="text-warning"
                        style={{ border: '1px solid #ffc107', backgroundColor: '#f8f9fa' }}
                        onClick={handleConfirmSubmit}
                    >
                        <i className="bi bi-check-lg me-1"></i>
                        Proceed Anyway
                    </Button>
                </Modal.Footer>
            </Modal>
        </Form>

        {/* Weight Capture Processing Modal */}
        <WeighbridgeCaptureModal show={isCapturing} weight={weightTrack} image={imageTrack} />
        {groupSelectModal}

        {/* E-Way bill details / recovery for sales that already have an outcome */}
        <EwayBillDetailDialog
            show={ewayDetailOpen}
            sale={originalData}
            onHide={() => setEwayDetailOpen(false)}
            onUpdated={() => {
                if (id) {
                    genericGetOne(ENTITY_ID_CUSTOMER_SALE, id)
                        .then(({ data }) => setOriginalData(data))
                        .catch(() => {});
                }
            }}
        />
        </>
    );
}

// Bill Calculations Component
function BillCalculations({ items, formik, taxPlansLookup = {}, payments = [], readOnly = false, createInvoice = false, roundOffMode = 'CEILING' }) {
    // Parse numbers safely (handles string inputs from form fields)
    const parseNumber = (value) => {
        if (value === null || value === undefined || value === '') return 0;
        const parsed = parseFloat(value);
        return isNaN(parsed) ? 0 : parsed;
    };

    // Format currency
    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined || isNaN(amount)) return '₹0.00';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount);
    };

    // Helper function to get tax percentage from lookup or from the tax object
    const getTaxPercentage = (tax) => {
        // First try to get from the tax object itself
        let percentage = tax.percentage || tax.taxPercentage;

        // If not found, try to look up by ID
        if ((percentage === null || percentage === undefined) && tax.id && taxPlansLookup[tax.id]) {
            percentage = taxPlansLookup[tax.id].taxPercentage;
        }

        // If still not found, try to look up by name (fallback)
        if ((percentage === null || percentage === undefined) && tax.name && taxPlansLookup[tax.name]) {
            percentage = taxPlansLookup[tax.name].taxPercentage;
        }

        return parseNumber(percentage);
    };

    // Helper function to get tax name from lookup or from the tax object
    const getTaxName = (tax) => {
        let name = tax.name;
        if (!name && tax.id && taxPlansLookup[tax.id]) {
            name = taxPlansLookup[tax.id].name || taxPlansLookup[tax.id].taxName;
        }
        name = name || 'Tax';
        if (name.includes('%')) return name;
        const percentage = getTaxPercentage(tax);
        return `${name} (${percentage}%)`;
    };


    // Calculate line items
    const lineItems = items.map(item => {
        const price = parseNumber(item.price);
        const quantity = parseNumber(item.quantity);
        const itemDiscount = parseNumber(item.discountAmount);
        const subtotal = price * quantity;
        const afterDiscount = subtotal - itemDiscount;

        // Calculate tax (only when createInvoice is true)
        let taxAmount = 0;
        const taxDetails = [];
        if (createInvoice && item.taxPlans && item.taxPlans.length > 0) {
            item.taxPlans.forEach(tax => {
                // Get tax percentage from lookup or from the tax object
                const taxPercent = getTaxPercentage(tax);
                const taxName = getTaxName(tax);
                const taxAmt = (afterDiscount * taxPercent) / 100;
                taxAmount += taxAmt;
                taxDetails.push({ name: taxName, percentage: taxPercent, amount: taxAmt });
            });
        }

        const lineTotal = afterDiscount + taxAmount;

        return {
            name: item.inventoryItemName || 'Item',
            itemType: item.itemType,
            price,
            quantity,
            unit: item.unitSymbol || '',
            subtotal,
            itemDiscount,
            afterDiscount,
            taxDetails,
            taxAmount,
            lineTotal
        };
    }).sort((a, b) => {
        // Sort INVENTORY items first, then SERVICE items
        if (a.itemType === 'INVENTORY' && b.itemType !== 'INVENTORY') return -1;
        if (a.itemType !== 'INVENTORY' && b.itemType === 'INVENTORY') return 1;
        return 0;
    });

    // Calculate totals
    const subtotal = lineItems.reduce((sum, item) => sum + item.subtotal, 0);
    const totalItemDiscounts = lineItems.reduce((sum, item) => sum + item.itemDiscount, 0);
    const totalTax = lineItems.reduce((sum, item) => sum + item.taxAmount, 0);
    const totalAfterTax = lineItems.reduce((sum, item) => sum + item.lineTotal, 0);

    // Auto-calculate round off based on ROUND_OFF_MODE setting
    const parsedRoundOff = computeRoundOffAmount(totalAfterTax, roundOffMode);
    const grandTotal = totalAfterTax + parsedRoundOff;

    // Aggregate taxes by tax plan name
    const taxBreakdown = {};
    lineItems.forEach(item => {
        item.taxDetails.forEach(tax => {
            const key = tax.name;
            if (!taxBreakdown[key]) {
                taxBreakdown[key] = { name: tax.name, percentage: tax.percentage, amount: 0 };
            }
            taxBreakdown[key].amount += tax.amount;
        });
    });
    const taxBreakdownList = Object.values(taxBreakdown);

    // Sync auto-calculated round off to formik
    useEffect(() => {
        formik.setFieldValue('roundOffAmount', parsedRoundOff);
    }, [totalAfterTax, roundOffMode]); // eslint-disable-line react-hooks/exhaustive-deps

    if (items.length === 0) {
        return (
            <div className="text-center text-muted py-3">
                <i className="bi bi-receipt fs-1 d-block mb-2 opacity-50"></i>
                <p className="mb-0">No items added yet</p>
                <small>Add items to see calculations</small>
            </div>
        );
    }

    return (
        <div className="bill-summary">
            {/* Line Items Table */}
            <div className="table-responsive">
                <table className="table table-sm mb-0" style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                            <th className="py-2 text-muted fw-normal" style={{ fontSize: '0.8rem', width: createInvoice ? '40%' : '45%' }}>Item</th>
                            <th className="py-2 text-muted fw-normal text-end" style={{ fontSize: '0.8rem', width: '20%' }}>Rate</th>
                            <th className="py-2 text-muted fw-normal text-center" style={{ fontSize: '0.8rem', width: '10%' }}>Qty</th>
                            {createInvoice && <th className="py-2 text-muted fw-normal text-end" style={{ fontSize: '0.8rem', width: '15%' }}>Tax</th>}
                            <th className="py-2 text-muted fw-normal text-end" style={{ fontSize: '0.8rem', width: createInvoice ? '15%' : '25%' }}>Amount</th>
                        </tr>
                    </thead>
                    <tbody>
                        {lineItems.map((item, index) => (
                            <tr key={index} style={{ borderBottom: '1px dashed #e9ecef' }}>
                                <td className="py-2 align-middle" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    <span className="fw-semibold" title={item.name}>{item.name}</span>
                                    {item.itemDiscount > 0 && (
                                        <small className="text-danger d-block">-{formatCurrency(item.itemDiscount)} disc</small>
                                    )}
                                </td>
                                <td className="text-end align-middle py-2">{formatCurrency(item.price)}</td>
                                <td className="text-center align-middle py-2">{parseFloat(item.quantity).toFixed(3)}</td>
                                {createInvoice && (
                                    <td className="text-end align-middle py-2">
                                        {item.taxAmount > 0 ? (
                                            <span className="text-success">{formatCurrency(item.taxAmount)}</span>
                                        ) : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                )}
                                <td className="text-end fw-semibold align-middle py-2">{formatCurrency(item.lineTotal)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Bill Summary Section */}
            <div className="mt-4 pt-3" style={{ borderTop: '2px solid #dee2e6' }}>
                {/* Subtotal */}
                <div className="d-flex justify-content-between py-2">
                    <span className="text-muted">Subtotal:</span>
                    <span className="fw-semibold">{formatCurrency(subtotal)}</span>
                </div>

                {/* Item Discounts */}
                {totalItemDiscounts > 0 && (
                    <div className="d-flex justify-content-between py-2" style={{ borderTop: '1px dotted #e9ecef' }}>
                        <span className="text-danger">Item Discounts:</span>
                        <span className="text-danger">-{formatCurrency(totalItemDiscounts)}</span>
                    </div>
                )}

                {/* Tax Breakdown */}
                {createInvoice && taxBreakdownList.length > 0 && (
                    <div style={{ borderTop: '1px dotted #e9ecef' }}>
                        {taxBreakdownList.map((tax, index) => (
                            <div key={index} className="d-flex justify-content-between py-2">
                                <span className="text-warning">{tax.name}:</span>
                                <span className="text-warning">{formatCurrency(tax.amount)}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Round Off (auto-calculated) */}
                {parsedRoundOff !== 0 && (
                    <div className="d-flex justify-content-between align-items-center py-2" style={{ borderTop: '1px dotted #e9ecef' }}>
                        <span className={parsedRoundOff >= 0 ? 'text-success' : 'text-danger'}>Round Off:</span>
                        <span className={`fw-semibold ${parsedRoundOff >= 0 ? 'text-success' : 'text-danger'}`}>
                            {parsedRoundOff >= 0 ? '+' : '-'}{formatCurrency(Math.abs(parsedRoundOff))}
                        </span>
                    </div>
                )}

                {/* Grand Total */}
                <div
                    className="d-flex justify-content-between align-items-center py-3 mt-2"
                    style={{
                        borderTop: '2px double #333',
                        borderBottom: '2px double #333',
                        backgroundColor: '#f8f9fa'
                    }}
                >
                    <span className="fw-bold fs-5">Grand Total:</span>
                    <span className="fw-bold fs-4 text-success">{formatCurrency(grandTotal)}</span>
                </div>

                {/* Payment Summary Section */}
                {payments.length === 0 && (
                    <div className="mt-3 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                        <div
                            className="d-flex justify-content-between align-items-center py-3"
                            style={{
                                backgroundColor: '#fff3cd',
                                margin: '0 -1rem',
                                padding: '0.75rem 1rem',
                                borderRadius: '0 0 8px 8px'
                            }}
                        >
                            <span className="fw-bold fs-6 text-danger">
                                <i className="bi bi-exclamation-triangle me-2"></i>Balance Due:
                            </span>
                            <span className="fw-bold fs-5 text-danger">{formatCurrency(grandTotal)}</span>
                        </div>
                    </div>
                )}

                {payments.length > 0 && (() => {
                    const totalPayments = payments.reduce((sum, p) => sum + parseNumber(p.totalAmount), 0);
                    const balanceAmount = grandTotal - totalPayments;

                    return (
                        <div className="mt-3 pt-3" style={{ borderTop: '1px solid #dee2e6' }}>
                            {/* Payment Breakdown Header */}
                            <div className="d-flex align-items-center mb-2">
                                <i className="bi bi-credit-card text-warning me-2"></i>
                                <span className="fw-semibold text-muted">Payment Details</span>
                            </div>

                            {/* Individual Payments */}
                            {payments.map((payment, index) => (
                                <div key={index} className="d-flex justify-content-between py-1 ps-3">
                                    <span className="text-muted" style={{ fontSize: '0.9rem' }}>
                                        {payment.ledgerAccountName || 'Payment ' + (index + 1)}
                                        {payment.referenceNumber && (
                                            <small className="text-secondary ms-1">({payment.referenceNumber})</small>
                                        )}
                                    </span>
                                    <span className="text-success" style={{ fontSize: '0.9rem' }}>
                                        {formatCurrency(parseNumber(payment.totalAmount))}
                                    </span>
                                </div>
                            ))}

                            {/* Total Payments */}
                            <div
                                className="d-flex justify-content-between align-items-center py-2 mt-2"
                                style={{ borderTop: '1px dashed #dee2e6' }}
                            >
                                <span className="fw-semibold text-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Total Paid:
                                </span>
                                <span className="fw-bold text-success">{formatCurrency(totalPayments)}</span>
                            </div>

                            {/* Balance Amount */}
                            <div
                                className="d-flex justify-content-between align-items-center py-3"
                                style={{
                                    borderTop: '2px solid #dee2e6',
                                    backgroundColor: balanceAmount > 0 ? '#fff3cd' : '#d1e7dd',
                                    margin: '0 -1rem',
                                    padding: '0.75rem 1rem',
                                    borderRadius: '0 0 8px 8px'
                                }}
                            >
                                <span className={`fw-bold fs-6 ${balanceAmount > 0 ? 'text-danger' : 'text-success'}`}>
                                    <i className={`bi ${balanceAmount > 0 ? 'bi-exclamation-triangle' : 'bi-check-circle-fill'} me-2`}></i>
                                    {balanceAmount > 0 ? 'Balance Due:' : balanceAmount < 0 ? 'Excess Paid:' : 'Fully Paid'}
                                </span>
                                <span className={`fw-bold fs-5 ${balanceAmount > 0 ? 'text-danger' : 'text-success'}`}>
                                    {balanceAmount !== 0 ? formatCurrency(Math.abs(balanceAmount)) : ''}
                                </span>
                            </div>
                        </div>
                    );
                })()}
            </div>
        </div>
    );
}

// Image thumbnail with overlay label and click-to-enlarge
function ImageThumbnail({ src, label, color, placeholder = false }) {
    const [showModal, setShowModal] = useState(false);

    if (!src) {
        return (
            <div
                className="border rounded d-flex flex-column align-items-center justify-content-center bg-light position-relative"
                style={{ height: '100px', borderStyle: 'dashed', borderColor: '#adb5bd' }}
            >
                {placeholder ? (
                    <>
                        <i className="bi bi-camera text-muted" style={{ fontSize: '1.5rem', opacity: 0.4 }}></i>
                        <span className="text-muted" style={{ fontSize: '0.65rem' }}>Awaiting capture</span>
                    </>
                ) : (
                    <span className="text-muted" style={{ fontSize: '0.7rem' }}>No image</span>
                )}
                <span
                    className="position-absolute bottom-0 start-0 end-0 text-center text-white fw-bold py-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.5)', fontSize: '0.7rem' }}
                >
                    {label}
                </span>
            </div>
        );
    }

    return (
        <>
            <div
                className="border rounded overflow-hidden position-relative"
                style={{ borderColor: color, cursor: 'pointer' }}
                onClick={() => setShowModal(true)}
            >
                <img
                    src={src}
                    alt={label}
                    style={{ width: '100%', height: 'auto', display: 'block' }}
                />
                <span
                    className="position-absolute bottom-0 start-0 end-0 text-center text-white fw-bold py-1"
                    style={{ backgroundColor: 'rgba(0,0,0,0.55)', fontSize: '0.75rem', letterSpacing: '0.5px' }}
                >
                    {label}
                </span>
            </div>
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton className="py-2">
                    <Modal.Title style={{ fontSize: '14px' }}>
                        <i className="bi bi-image me-2"></i>{label} Weight Image
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    <img src={src} alt={label} style={{ width: '100%', height: 'auto', display: 'block' }} />
                </Modal.Body>
            </Modal>
        </>
    );
}
